import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Paper, Container,
  Typography, CircularProgress, IconButton, TextField, Button, Box,
  MenuItem, Select, Alert, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Edit, Delete, Save, Close } from '@mui/icons-material';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const MetalIssueList = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const karigar = params.get('karigar') || '';
  const karat = params.get('karat') || '';

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchIssues = () => {
    setLoading(true);
    api.get(`/api/karigar/metal-issue-list?karigar=${karigar}&karat=${karat}`)
      .then(res => setIssues(res.data))
      .catch(() => setIssues([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchIssues(); }, [karigar, karat]);

  const startEdit = (issue) => {
    setEditId(issue._id);
    setEditData({ karat: issue.karat, amount: issue.amount, date: issue.date, remarks: issue.remarks || '' });
    setError('');
  };

  const cancelEdit = () => { setEditId(null); setEditData({}); };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/api/karigar/metal-issue/${editId}`, editData);
      setEditId(null);
      fetchIssues();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/api/karigar/metal-issue/${deleteId}`);
      setDeleteId(null);
      fetchIssues();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
    setDeleting(false);
  };

  const total = issues.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h5" gutterBottom>Metal Issue List
        {karigar && <Typography component="span" variant="subtitle1" sx={{ ml: 1, color: 'text.secondary' }}>— {karigar}{karat ? ` / ${karat}` : ''}</Typography>}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        {loading ? <CircularProgress sx={{ m: 2 }} /> : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                <TableCell sx={{ fontWeight: 700 }}>Karigar</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Karat</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount (g)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">No metal issues found.</TableCell></TableRow>
              ) : issues.map(issue => (
                <TableRow key={issue._id} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                  <TableCell>{issue.karigarAbbreviation}</TableCell>

                  {editId === issue._id ? (
                    <>
                      <TableCell>
                        <Select size="small" value={editData.karat} onChange={e => setEditData(d => ({ ...d, karat: e.target.value }))} sx={{ minWidth: 90 }}>
                          {karatOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={editData.amount} onChange={e => setEditData(d => ({ ...d, amount: e.target.value }))} sx={{ width: 90 }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" value={editData.date} onChange={e => setEditData(d => ({ ...d, date: e.target.value }))} sx={{ width: 120 }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" value={editData.remarks} onChange={e => setEditData(d => ({ ...d, remarks: e.target.value }))} sx={{ width: 140 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}><Save fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={cancelEdit}><Close fontSize="small" /></IconButton>
                        </Box>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{issue.karat}</TableCell>
                      <TableCell>{issue.amount}</TableCell>
                      <TableCell>{issue.date}</TableCell>
                      <TableCell>{issue.remarks}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton size="small" color="primary" onClick={() => startEdit(issue)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteId(issue._id)}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
              {issues.length > 0 && (
                <TableRow sx={{ backgroundColor: '#e8e8e8' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{total.toFixed(3)}</TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Metal Issue?</DialogTitle>
        <DialogContent>
          <Typography>This will remove the entry and adjust the karigar's total given amount. Are you sure?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MetalIssueList;
